import Select, { StylesConfig } from '@databases/atlaskit/Select';
import { useFlaggingCallback } from '@databases/banners';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { useScope } from '@databases/scope';
import { DistributiveOmit } from '@databases/types';
import { DatabaseFieldType } from '@databases/types/database';
import { DatabaseFilter, DatabaseFilterType } from '@databases/types/database/view';
import { getDatabaseFieldOptional } from '@databases/utils/database/utils';
import { isEqual, omit, orderBy } from 'lodash-es';
import React, { FunctionComponent, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { updateFilterType } from '../hooks/updateFilterType';
import { filterTypeLabelMap } from '../hooks/useFilterTypeLabel';

export type FilterType = DistributiveOmit<DatabaseFilter, 'value' | 'id'>;

type FilterTypeOption = DistributiveOmit<FilterType, 'fieldId'> & { label: string };

const getOptionValue = (op: FilterTypeOption): string => op.label;

const styles: StylesConfig<FilterTypeOption> = {
  menu: (base) => ({ ...base, width: 'max-content', minWidth: '100%' }),
};

export interface FilterTypePickerProps {
  value: DatabaseFilter;
  onChange: (value: DatabaseFilter) => void;
  contentId: string;

  /** Which filter types to offer in the select box. If this is undefined, all filter types are shown. */
  types?: DatabaseFilterType[];
}

/**
 * Renders a select box where a database filter type can be selected.
 */
export const FilterTypePicker: FunctionComponent<FilterTypePickerProps> = ({
  value,
  onChange,
  types,
  contentId,
}) => {
  const store = defaultStoreUtils.useStore();
  const scope = useScope();
  const handleChange = useFlaggingCallback(
    (op: FilterTypeOption | null) => {
      onChange(updateFilterType(value, omit(op!, 'label'), contentId, store.getState()));
    },
    [contentId, onChange, store, value]
  );

  const field = defaultStoreUtils.useSelector((state) => {
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      scope,
      includeDeleted: true,
    });
    return getDatabaseFieldOptional(databaseDraft, (value as any).fieldId);
  });

  const { formatMessage } = useIntl();

  const filterTypeOptions = useMemo((): FilterTypeOption[] => {
    const options = [
      {
        label: filterTypeLabelMap[DatabaseFilterType.EQUALS]({
          fieldType: field?.type,
          formatMessage,
        }),
        type: DatabaseFilterType.EQUALS,
      },
      {
        label: filterTypeLabelMap[DatabaseFilterType.EQUALS]({
          fieldType: field?.type,
          formatMessage,
          not: true,
        }),
        type: DatabaseFilterType.EQUALS,
        not: true,
      },
      {
        label: filterTypeLabelMap[DatabaseFilterType.CONTAINS]({
          fieldType: field?.type,
          formatMessage,
        }),
        type: DatabaseFilterType.CONTAINS,
      },
      {
        label: filterTypeLabelMap[DatabaseFilterType.CONTAINS]({
          fieldType: field?.type,
          formatMessage,
          not: true,
        }),
        type: DatabaseFilterType.CONTAINS,
        not: true,
      },
      {
        label: filterTypeLabelMap[DatabaseFilterType.EMPTY]({
          fieldType: field?.type,
          formatMessage,
        }),
        type: DatabaseFilterType.EMPTY,
      },
      {
        label: filterTypeLabelMap[DatabaseFilterType.EMPTY]({
          fieldType: field?.type,
          formatMessage,
          not: true,
        }),
        type: DatabaseFilterType.EMPTY,
        not: true,
      },
      {
        label: filterTypeLabelMap[DatabaseFilterType.GREATER]({
          fieldType: field?.type,
          formatMessage,
        }),
        type: DatabaseFilterType.GREATER,
        orEqual: false,
      },
      {
        label: filterTypeLabelMap[DatabaseFilterType.GREATER]({
          fieldType: field?.type,
          formatMessage,
          orEqual: true,
        }),
        type: DatabaseFilterType.GREATER,
        orEqual: true,
      },
      {
        label: filterTypeLabelMap[DatabaseFilterType.LESS]({
          fieldType: field?.type,
          formatMessage,
        }),
        type: DatabaseFilterType.LESS,
        orEqual: false,
      },
      {
        label: filterTypeLabelMap[DatabaseFilterType.LESS]({
          fieldType: field?.type,
          formatMessage,
          orEqual: true,
        }),
        type: DatabaseFilterType.LESS,
        orEqual: true,
      },
    ];
    return field?.type === DatabaseFieldType.NUMBER
      ? orderBy(options, (option) => option.type, 'asc')
      : options;
  }, [field?.type, formatMessage]);

  const availableOptions = useMemo(
    () =>
      types
        ? filterTypeOptions.filter((op) => types.includes(op.type))
        : filterTypeOptions,
    [filterTypeOptions, types]
  );

  return (
    <Select<FilterTypeOption>
      options={availableOptions}
      value={filterTypeOptions.find((op) =>
        isEqual(omit(op, ['label']), omit(value, ['fieldId', 'value', 'id']))
      )}
      onChange={handleChange}
      getOptionValue={getOptionValue}
      styles={styles}
      inputId="filter-value-edit-type-select"
    />
  );
};
